// App UI translations (#49c). English strings ARE the keys — t("Delete")
// returns "Supprimer" in French, or the key itself when no translation
// exists yet. That makes partial translation safe: untranslated strings
// simply render in English, and adding a language later (e.g. pt-PT) is
// just another column here.

export type Lang = "en" | "fr" | "pt";

const STORAGE_KEY = "esprey.lang";

const VALID: Lang[] = ["en", "fr", "pt"];

let current: Lang = (() => {
  try {
    const v = localStorage.getItem(STORAGE_KEY) as Lang | null;
    return v && VALID.includes(v) ? v : "en";
  } catch {
    return "en";
  }
})();

export function getLang(): Lang {
  return current;
}

/** Set the UI language (persisted per device; synced from the profile on boot). */
export function setLang(l: Lang) {
  current = VALID.includes(l) ? l : "en";
  try { localStorage.setItem(STORAGE_KEY, current); } catch { /* ignore */ }
}

export function t(s: string): string {
  if (current === "en") return s;
  const dict = current === "fr" ? FR : PT;
  return dict[s] ?? s;
}

const FR: Record<string, string> = {
  // ----- Chrome / navigation -----
  "Expenses": "Dépenses",
  "Settings": "Réglages",
  "Reports": "Rapports",
  "Help & FAQ": "Aide & FAQ",
  "Help": "Aide",
  "Total of": "Total de",
  "receipts in view · one line per currency": "reçus affichés · une ligne par devise",
  "1 receipt has no amount and is not counted.": "1 reçu n'a pas de montant et n'est pas compté.",
  "receipts have no amount and are not counted.": "reçus n'ont pas de montant et ne sont pas comptés.",
  "1 without amount": "1 sans montant",
  "without amount": "sans montant",
  "No date": "Sans date",
  "Wrong month?": "Mauvais mois ?",
  "Duplicate?": "Doublon ?",
  "Can't convert": "Non convertible",
  "Over limit": "Au-dessus du plafond",
  "Edited vs OCR": "Modifié vs lecture",
  "Late photo": "Photo tardive",
  "Status": "Statut",
  "Before you send": "Avant d'envoyer les rapports de",
  "'s reports": "",
  "receipt needs a look": "reçu à vérifier",
  "receipts need a look": "reçus à vérifier",
  "has no company yet": "sans société",
  "have no company yet": "sans société",
  "and": "et",
  "Resolve": "Vérifier",
  "Assign": "Attribuer",
  "Check the photo": "Vérifiez la photo",
  "Take the first page, then tap “+ Add page” to keep going. All pages become one PDF receipt.": "Prenez la première page, puis touchez « + Ajouter une page » pour continuer. Toutes les pages forment un seul reçu PDF.",
  "Several receipts at once?": "Plusieurs reçus à la fois ?",
  "In the file picker, select several files. Each becomes its own receipt.": "Dans le sélecteur de fichiers, choisissez plusieurs fichiers. Chacun devient un reçu.",
  "No signal?": "Pas de réseau ?",
  "Take the photo with your camera app and email it here. Your outbox sends it when you're back online.": "Prenez la photo avec l'appareil photo et envoyez-la ici par e-mail. Votre boîte d'envoi l'expédiera au retour du réseau.",
  "Copy": "Copier",
  "Copied": "Copié",
  "After Save, Claude reads it in about 5–10 seconds and opens the receipt.": "Après Enregistrer, Claude la lit en 5 à 10 secondes et ouvre le reçu.",
  "Add page": "Ajouter une page",
  "View full size": "Taille réelle",
  "Rotate": "Pivoter",
  "Photographed": "Photographié le",
  "Uploaded": "Ajouté le",
  "Read by Claude": "Lu par Claude",
  "Entered by hand": "Saisi à la main",
  "Custom…": "Autre…",
  "Needs a look": "À vérifier",
  "receipt": "reçu",
  "receipts": "reçus",
  "more": "autres",
  "less": "réduire",
  "Add a receipt": "Ajouter un reçu",
  "Account menu": "Menu du compte",
  "Your account": "Votre compte",
  "Sign out": "Se déconnecter",
  "← Back": "← Retour",
  "Back": "Retour",
  "Loading…": "Chargement…",
  "Open": "Ouvrir",
  "Refresh": "Actualiser",
  "Cancel": "Annuler",
  "Delete": "Supprimer",
  "Save": "Enregistrer",
  "Saving…": "Enregistrement…",
  "Save & back": "Enregistrer et retour",
  "+ Manual": "+ Saisie",
  "+ Capture": "+ Photo",
  "Manual": "Saisie",
  "Capture": "Photo",

  // ----- Dashboard -----
  "Receipts": "Reçus",
  "Uncategorized": "Sans catégorie",
  "Issues": "Anomalies",
  "Filter by company": "Filtrer par société",
  "All companies": "Toutes les sociétés",
  "Clear filters": "Effacer les filtres",
  "Show all receipts again": "Afficher de nouveau tous les reçus",
  "Filter by date range": "Filtrer par période",
  "All time": "Toute la période",
  "This week": "Cette semaine",
  "Last week": "Semaine dernière",
  "This month": "Ce mois-ci",
  "Last month": "Mois dernier",
  "Last 30 days": "30 derniers jours",
  "Last 90 days": "90 derniers jours",
  "Custom (pick dates)": "Personnalisé (choisir les dates)",
  "Pick dates": "Choisir les dates",
  "Custom date range": "Période personnalisée",
  "Edit custom date range": "Modifier la période",
  "From": "Du",
  "To": "Au",
  "Done": "OK",
  "Date": "Date",
  "Vendor": "Fournisseur",
  "Amount": "Montant",
  "Currency": "Devise",
  "Category": "Catégorie",
  "Company": "Société",
  "Description": "Description",
  "Notes": "Notes",
  "OCR failed": "Échec de lecture",
  "Possible duplicate": "Doublon possible",
  "No amount": "Montant manquant",
  "Over category limit": "Au-dessus de la limite",
  "Currency can't be converted — left out of report totals": "Devise non convertible — exclue des totaux du rapport",
  "Edited values differ from OCR": "Valeurs modifiées ≠ lecture du reçu",
  "Reassign company…": "Réaffecter la société…",
  "Reassign category…": "Réaffecter la catégorie…",
  "Capture your first one →": "Photographiez votre premier reçu →",
  "Capture your first one": "Photographiez votre premier reçu",

  // ----- Capture -----
  "Capture receipt": "Photographier un reçu",
  "Snap a photo of your receipt — Claude will read it.": "Prenez votre reçu en photo — Claude le lit automatiquement.",
  "Add another page": "Ajouter une page",
  "Retake": "Reprendre",
  "Start over": "Recommencer",
  "Uploading & reading…": "Envoi et lecture…",
  "Working…": "Traitement…",
  "Reading…": "Lecture…",
  "Multi-page invoice?": "Facture de plusieurs pages ?",
  "Bulk upload tip:": "Astuce envoi groupé :",
  "Camera app": "appareil photo",
  "PDF file": "Fichier PDF",
  "Claude will read the PDF contents.": "Claude lira le contenu du PDF.",
  "No preview available.": "Aperçu indisponible.",

  // ----- Manual entry -----
  "Manual entry": "Saisie manuelle",
  "Save expense": "Enregistrer la dépense",
  "Amount *": "Montant *",
  "What was this for?": "C'était pour quoi ?",
  "e.g. Cash taxi, Coffee shop": "ex. Taxi en espèces, Café",

  // ----- Receipt detail -----
  "Receipt": "Reçu",
  "People present": "Personnes présentes",
  "Tip": "Pourboire",
  "No tip": "Sans pourboire",
  "Custom amount…": "Montant personnalisé…",
  "Tip amount": "Montant du pourboire",
  "e.g. 5.00": "ex. 5,00",
  "Bill (from receipt):": "Addition (sur le reçu) :",
  "Total (saved to report):": "Total (enregistré) :",
  "Why this receipt is flagged": "Pourquoi ce reçu est signalé",
  "Acknowledge — this is a separate expense": "Confirmer — c'est une dépense distincte",
  "Acknowledge — I know this is over the limit": "Confirmer — je sais que la limite est dépassée",
  "Acknowledge override": "Confirmer la modification",

  // ----- Reports -----
  "Monthly reports": "Rapports mensuels",
  "Generate a report": "Générer un rapport",
  "Month": "Mois",
  "Report language": "Langue du rapport",
  "All companies (combined PDF)": "Toutes les sociétés (PDF combiné)",
  "All currencies": "Toutes les devises",
  "Generate": "Générer",
  "Generating…": "Génération…",
  "Open PDF": "Ouvrir le PDF",
  "Download PDF": "Télécharger le PDF",
  "Emailing…": "Envoi…",
  "Personal": "Personnel",
  "Open camera": "Ouvrir l'appareil photo",
  "Pick photo(s) or PDF from files": "Choisir photo(s) ou PDF depuis les fichiers",
  "Building PDF & uploading…": "Création du PDF et envoi…",
  "Save & close": "Enregistrer et fermer",
  "Cur": "Dev",
  "Custom range…": "Période personnalisée…",
  "Descriptions and categories are translated. Establishment names stay exactly as printed on the receipts.": "Les descriptions et catégories sont traduites. Les noms des établissements restent exactement tels qu'imprimés sur les reçus.",
  // ----- Settings -----
  "How this works": "Comment ça marche",
  "Searchable answers + ask-a-question box": "Réponses consultables + boîte à questions",
  "Team": "Équipe",
  "Manage team members": "Gérer les membres de l'équipe",
  "Add or remove people who can sign in": "Ajouter ou retirer des personnes",
  "My profile": "Mon profil",
  "Personal details": "Informations personnelles",
  "Name, address, bank details (used on invoices)": "Nom, adresse, coordonnées bancaires (pour les factures)",
  "Edit": "Modifier",
  "Trash": "Corbeille",
  "Deleted receipts appear here for 30 days, then they're gone for good.": "Les reçus supprimés restent ici 30 jours, puis disparaissent définitivement.",
  "Unknown vendor": "Fournisseur inconnu",
  "Restore": "Restaurer",
  "Restoring…": "Restauration…",
  "Companies": "Sociétés",
  "Categories": "Catégories",
  "People": "Personnes",
  "Currencies": "Devises",
  "No entries yet.": "Aucune entrée pour l'instant.",
  "+ Add company": "+ Ajouter une société",
  "Add a person": "Ajouter une personne",
  "Currency name": "Nom de la devise",
  "New category name": "Nom de la nouvelle catégorie",
  "No limit": "Sans limite",
  "Add": "Ajouter",
  "+ Add category": "+ Ajouter une catégorie",
  "Spending limit is per receipt. Anything over it gets flagged in Issues until the team member acknowledges it.": "La limite de dépense s'applique par reçu. Tout dépassement est signalé dans Anomalies jusqu'à confirmation par le membre de l'équipe.",
  "Categories are managed by the admin. Ask Carl to add a new one if you need it.": "Les catégories sont gérées par l'administrateur. Demandez à Carl d'en ajouter une si besoin.",
  // ----- My profile -----
  "App language": "Langue de l'application",
  "Identity": "Identité",
  "Address": "Adresse",
  "Payment details": "Coordonnées de paiement",
  "Payment instructions": "Instructions de paiement",
  "Full name": "Nom complet",
  "Business name": "Raison sociale",
  "Email": "E-mail",
  "Phone": "Téléphone",
  "Address line 1": "Adresse ligne 1",
  "Address line 2": "Adresse ligne 2",
  "Country": "Pays",
  "VAT / Tax number": "Numéro de TVA",
  "Your full name": "Votre nom complet",
  "Street address": "Adresse (rue)",
  "City, region, postcode": "Ville, région, code postal",
  "Your country": "Votre pays",
  "Optional": "Facultatif",
  "Translating…": "Traduction…",
  "Translate my existing receipt descriptions": "Traduire les descriptions de mes reçus existants",
  // ----- Prose, banners, dialogs, errors -----
  "Language": "Langue",
  "Amount must be a positive number (e.g. 12.50). Letters aren't allowed.": "Le montant doit être un nombre positif (ex. 12,50). Les lettres ne sont pas autorisées.",
  "Amount must be a positive number (e.g. 12.50).": "Le montant doit être un nombre positif (ex. 12,50).",
  "Receipt date is in the future — please pick today or earlier.": "La date du reçu est dans le futur — choisissez aujourd'hui ou une date antérieure.",
  "Delete this receipt? It moves to Trash for 30 days, then it's gone for good.": "Supprimer ce reçu ? Il sera placé dans la corbeille pendant 30 jours, puis définitivement supprimé.",
  "OCR vs your edits — please review": "Lecture du reçu vs vos modifications — veuillez vérifier",
  "OCR failed to process this receipt. Fill in the amount, currency, and date manually below.": "La lecture automatique de ce reçu a échoué. Saisissez le montant, la devise et la date manuellement ci-dessous.",
  "No amount was extracted from this receipt. Enter the amount manually in the Amount field below.": "Aucun montant n'a été extrait de ce reçu. Saisissez-le manuellement dans le champ Montant ci-dessous.",
  "Clicking confirms you're intentionally claiming this even though it matches another receipt. If it really is a duplicate, delete one instead.": "Cliquer confirme que vous soumettez volontairement cette dépense bien qu'elle corresponde à un autre reçu. S'il s'agit vraiment d'un doublon, supprimez-en un.",
  "Clicking records that you're knowingly claiming an over-limit expense and clears the Issues flag. The acknowledgement stays on the receipt's record.": "Cliquer enregistre que vous soumettez sciemment une dépense au-dessus de la limite et retire le signalement. La confirmation reste dans l'historique du reçu.",
  "This receipt is": "Ce reçu est de",
  "over the": "au-dessus de la limite de",
  "limit for": "pour",
  "Over the spending limit": "Limite de dépense dépassée",
  "No internet detected.": "Pas de connexion internet.",
  "Photos taken in this app are not yet saved while offline — they'd be lost when the upload fails. As a fallback right now: take the photo with your phone's Camera app, then email it to": "Les photos prises dans l'application ne sont pas encore sauvegardées hors ligne — elles seraient perdues si l'envoi échoue. En attendant : prenez la photo avec l'appareil photo de votre téléphone, puis envoyez-la par e-mail à",
  "Your mail app's outbox will queue and send it when you're back online.": "Votre application e-mail l'enverra automatiquement dès le retour de la connexion.",
  "Open camera, take the first page, then tap \"+ Add another page\" on the preview to keep going. All pages are combined into one PDF receipt.": "Ouvrez l'appareil photo, prenez la première page, puis touchez « + Ajouter une page » sur l'aperçu pour continuer. Toutes les pages sont combinées en un seul reçu PDF.",
  "in the file picker, tap-and-hold on iPhone or Cmd-click on Mac to select multiple files at once. Each becomes its own receipt.": "dans le sélecteur de fichiers, maintenez le doigt (iPhone) ou Cmd-clic (Mac) pour sélectionner plusieurs fichiers à la fois. Chacun devient un reçu distinct.",
  "No signal? Use your Camera app and email to": "Pas de réseau ? Utilisez votre appareil photo et envoyez par e-mail à",
  "These details appear at the top of every monthly invoice (BILL FROM block) and in the payment-details footer.": "Ces informations apparaissent en haut de chaque facture mensuelle et dans le pied de page des coordonnées de paiement.",
  // ----- Help & FAQ page -----
  "Search the FAQ… (e.g. duplicate, email, tip)": "Rechercher dans la FAQ… (ex. doublon, e-mail, pourboire)",
  "Nothing matches": "Aucun résultat pour",
  "Try the question box above, or email": "Essayez la boîte à questions ci-dessus, ou écrivez à",
  "How do I…?": "Comment faire… ?",
  "Ask anything — e.g. how do I forward a receipt by email?": "Posez votre question — ex. comment transférer un reçu par e-mail ?",
  "Thinking…": "Réflexion…",
  "Ask": "Demander",
  "Answers usage questions only — it can't see your receipts or change anything.": "Répond uniquement aux questions d'utilisation — sans accès à vos reçus et sans rien modifier.",
  // ----- Concierge -----
  "Concierge": "Concierge",
  "Send": "Envoyer",
  "Message the Concierge…": "Écrivez au Concierge…",
  "Ask about your receipts…": "Posez une question sur vos reçus…",
  "Ask me about your expenses, or tell me to record one:": "Posez-moi une question sur vos dépenses, ou demandez-moi d'en enregistrer une :",
  "How much did I spend on meals last month?": "Combien ai-je dépensé en repas le mois dernier ?",
  "Record a 4.50 coffee at Starbeans today, category Meals": "Enregistre un café à 4,50 chez Starbeans aujourd'hui, catégorie Repas",
  "Which receipts still have issues?": "Quels reçus ont encore des anomalies ?",
  "I can only see and change YOUR receipts. Team and settings changes happen in Settings.": "Je ne vois et ne modifie que VOS reçus. L'équipe et les réglages se gèrent dans Réglages.",
  "Confirm delete": "Confirmer la suppression",
  "Done — moved to Trash (restorable for 30 days).": "C'est fait — déplacé dans la corbeille (restaurable pendant 30 jours).",
  "Okay — cancelled, nothing was deleted.": "D'accord — annulé, rien n'a été supprimé.",
  "Smart AI": "Smart AI",
  "Receipt descriptions are also written in this language.": "Les descriptions des reçus seront aussi rédigées dans cette langue.",
  // ----- Sweep 2026-07-04 (Carl's Portuguese test) -----
  "No receipts match these filters.": "Aucun reçu ne correspond à ces filtres.",
  "No receipts yet.": "Aucun reçu pour l'instant.",
  "Loading PDF…": "Chargement du PDF…",
  "OCR extracted": "Lecture du reçu",
  "You entered": "Votre saisie",
  "Tap a company name to edit address & full legal name (used in invoices).": "Touchez le nom d'une société pour modifier l'adresse et la raison sociale (utilisées dans les factures).",
  "Companies are managed by the admin. Ask Carl to add a new one if you need it.": "Les sociétés sont gérées par l'administrateur. Demandez à Carl d'en ajouter une si besoin.",
  "Currencies are managed by the admin. Ask Carl to add a new one if you need it.": "Les devises sont gérées par l'administrateur. Demandez à Carl d'en ajouter une si besoin.",
  "Free-form text — printed word-for-word in the PAYMENT DETAILS block of every invoice. Format it however your bank/country requires (IBAN + SWIFT, sort code + account, etc.).": "Texte libre — imprimé tel quel dans le bloc COORDONNÉES DE PAIEMENT de chaque facture. Formatez-le selon les exigences de votre banque/pays (IBAN + SWIFT, sort code + compte, etc.).",
  "Optional — for tax-registered users": "Facultatif — si vous êtes assujetti à la TVA",
  "Optional — if you invoice through a company": "Facultatif — si vous facturez via une société",
  "Contact email shown on invoice": "E-mail de contact affiché sur la facture",
  "Rewrite the descriptions of ALL your existing receipts in this language? Vendor names, amounts and dates are never changed. This can't be undone.": "Réécrire les descriptions de TOUS vos reçus existants dans cette langue ? Les noms des fournisseurs, montants et dates ne changent jamais. Action irréversible.",
  "descriptions translated.": "descriptions traduites.",
  "Translation failed:": "Échec de la traduction :",
  "Type a name — pick from the list, or hit Add to create": "Saisissez un nom — choisissez dans la liste ou touchez Ajouter",
  "Multi-page document": "Document de plusieurs pages",
  "page": "page",
  "pages": "pages",
  "Page": "Page",
  // ----- Guided tour (#48a) -----
  "Skip": "Passer",
  "Next": "Suivant",
  "Got it!": "C'est parti !",
  "Show me around again": "Refaire la visite guidée",
  "Add receipts": "Ajouter des reçus",
  "Tap + Capture to photograph a receipt — the amount, vendor and date are read for you. You can also email receipts in, or add cash expenses manually.": "Touchez + Photo pour photographier un reçu — le montant, le fournisseur et la date sont lus pour vous. Vous pouvez aussi envoyer des reçus par e-mail ou saisir des dépenses en espèces manuellement.",
  "Your counters": "Vos compteurs",
  "Receipts shows everything. Uncategorized still needs a company. Issues turns red when something needs your attention — tap it to see why.": "Reçus montre tout. Sans catégorie attend une société. Anomalies devient rouge quand quelque chose demande votre attention — touchez-le pour voir pourquoi.",
  "At month-end, generate a polished PDF of your expenses here — in the language of your choice.": "En fin de mois, générez ici un PDF soigné de vos dépenses — dans la langue de votre choix.",
  "Your assistant": "Votre assistant",
  "Ask anything — \"how much did I spend on meals?\" — or tell it to record an expense for you. It only ever sees your own receipts.": "Posez n'importe quelle question — « combien ai-je dépensé en repas ? » — ou demandez-lui d'enregistrer une dépense. Il ne voit que vos propres reçus.",
  "Need help?": "Besoin d'aide ?",
  "The ? opens the FAQ with an ask-a-question box. You can rerun this tour from there anytime.": "Le ? ouvre la FAQ avec une boîte à questions. Vous pouvez relancer cette visite depuis là à tout moment.",
  "Try reading it again": "Réessayer la lecture",
};


const PT: Record<string, string> = {
  // ----- Chrome / navigation -----
  "Expenses": "Despesas",
  "Settings": "Definições",
  "Reports": "Relatórios",
  "Help & FAQ": "Ajuda e FAQ",
  "Help": "Ajuda",
  "Total of": "Total de",
  "receipts in view · one line per currency": "recibos em vista · uma linha por moeda",
  "1 receipt has no amount and is not counted.": "1 recibo não tem valor e não é contado.",
  "receipts have no amount and are not counted.": "recibos não têm valor e não são contados.",
  "1 without amount": "1 sem valor",
  "without amount": "sem valor",
  "No date": "Sem data",
  "Wrong month?": "Mês errado?",
  "Duplicate?": "Duplicado?",
  "Can't convert": "Não convertível",
  "Over limit": "Acima do limite",
  "Edited vs OCR": "Editado vs leitura",
  "Late photo": "Foto tardia",
  "Status": "Estado",
  "Before you send": "Antes de enviar os relatórios de",
  "'s reports": "",
  "receipt needs a look": "recibo a verificar",
  "receipts need a look": "recibos a verificar",
  "has no company yet": "sem empresa",
  "have no company yet": "sem empresa",
  "and": "e",
  "Resolve": "Verificar",
  "Assign": "Atribuir",
  "Check the photo": "Verifique a foto",
  "Take the first page, then tap “+ Add page” to keep going. All pages become one PDF receipt.": "Fotografe a primeira página e toque em “+ Adicionar página” para continuar. Todas as páginas ficam num único recibo PDF.",
  "Several receipts at once?": "Vários recibos de uma vez?",
  "In the file picker, select several files. Each becomes its own receipt.": "No seletor de ficheiros, escolha vários ficheiros. Cada um passa a ser um recibo.",
  "No signal?": "Sem rede?",
  "Take the photo with your camera app and email it here. Your outbox sends it when you're back online.": "Tire a foto com a câmara e envie-a por e-mail para aqui. A caixa de saída envia-a quando voltar a ter rede.",
  "Copy": "Copiar",
  "Copied": "Copiado",
  "After Save, Claude reads it in about 5–10 seconds and opens the receipt.": "Depois de Guardar, o Claude lê-o em 5 a 10 segundos e abre o recibo.",
  "Add page": "Adicionar página",
  "View full size": "Tamanho real",
  "Rotate": "Rodar",
  "Photographed": "Fotografado em",
  "Uploaded": "Adicionado em",
  "Read by Claude": "Lido pelo Claude",
  "Entered by hand": "Inserido à mão",
  "Custom…": "Outro…",
  "Needs a look": "A verificar",
  "receipt": "recibo",
  "receipts": "recibos",
  "more": "mais",
  "less": "menos",
  "Add a receipt": "Adicionar recibo",
  "Account menu": "Menu da conta",
  "Your account": "A sua conta",
  "Sign out": "Terminar sessão",
  "← Back": "← Voltar",
  "Back": "Voltar",
  "Loading…": "A carregar…",
  "Open": "Abrir",
  "Refresh": "Atualizar",
  "Cancel": "Cancelar",
  "Delete": "Eliminar",
  "Save": "Guardar",
  "Saving…": "A guardar…",
  "Save & back": "Guardar e voltar",
  "+ Manual": "+ Manual",
  "+ Capture": "+ Foto",
  "Manual": "Manual",
  "Capture": "Foto",

  // ----- Dashboard -----
  "Receipts": "Recibos",
  "Uncategorized": "Sem categoria",
  "Issues": "Anomalias",
  "Filter by company": "Filtrar por empresa",
  "All companies": "Todas as empresas",
  "Clear filters": "Limpar filtros",
  "Show all receipts again": "Mostrar novamente todos os recibos",
  "Filter by date range": "Filtrar por período",
  "All time": "Todo o período",
  "This week": "Esta semana",
  "Last week": "Semana passada",
  "This month": "Este mês",
  "Last month": "Mês passado",
  "Last 30 days": "Últimos 30 dias",
  "Last 90 days": "Últimos 90 dias",
  "Custom (pick dates)": "Personalizado (escolher datas)",
  "Pick dates": "Escolher datas",
  "Custom date range": "Período personalizado",
  "Edit custom date range": "Editar o período",
  "From": "De",
  "To": "Até",
  "Done": "OK",
  "Date": "Data",
  "Vendor": "Fornecedor",
  "Amount": "Montante",
  "Currency": "Moeda",
  "Category": "Categoria",
  "Company": "Empresa",
  "Description": "Descrição",
  "Notes": "Notas",
  "OCR failed": "Falha na leitura",
  "Possible duplicate": "Possível duplicado",
  "No amount": "Montante em falta",
  "Over category limit": "Acima do limite",
  "Currency can't be converted — left out of report totals": "Moeda não convertível — excluída dos totais do relatório",
  "Edited values differ from OCR": "Valores editados ≠ leitura do recibo",
  "Reassign company…": "Reatribuir empresa…",
  "Reassign category…": "Reatribuir categoria…",
  "Capture your first one →": "Fotografe o seu primeiro recibo →",
  "Capture your first one": "Fotografe o seu primeiro recibo",

  // ----- Capture -----
  "Capture receipt": "Fotografar recibo",
  "Snap a photo of your receipt — Claude will read it.": "Tire uma foto do recibo — o Claude lê-o automaticamente.",
  "Add another page": "Adicionar página",
  "Retake": "Repetir",
  "Start over": "Recomeçar",
  "Uploading & reading…": "A enviar e ler…",
  "Working…": "A processar…",
  "Reading…": "A ler…",
  "Multi-page invoice?": "Fatura com várias páginas?",
  "Bulk upload tip:": "Dica de envio em massa:",
  "Camera app": "câmara",
  "PDF file": "Ficheiro PDF",
  "Claude will read the PDF contents.": "O Claude lerá o conteúdo do PDF.",
  "No preview available.": "Pré-visualização indisponível.",

  // ----- Manual entry -----
  "Manual entry": "Registo manual",
  "Save expense": "Guardar despesa",
  "Amount *": "Montante *",
  "What was this for?": "Para que foi isto?",
  "e.g. Cash taxi, Coffee shop": "ex. Táxi em dinheiro, Café",

  // ----- Receipt detail -----
  "Receipt": "Recibo",
  "People present": "Pessoas presentes",
  "Tip": "Gorjeta",
  "No tip": "Sem gorjeta",
  "Custom amount…": "Montante personalizado…",
  "Tip amount": "Valor da gorjeta",
  "e.g. 5.00": "ex. 5,00",
  "Bill (from receipt):": "Conta (no recibo):",
  "Total (saved to report):": "Total (guardado):",
  "Why this receipt is flagged": "Porque está este recibo sinalizado",
  "Acknowledge — this is a separate expense": "Confirmar — é uma despesa distinta",
  "Acknowledge — I know this is over the limit": "Confirmar — sei que ultrapassa o limite",
  "Acknowledge override": "Confirmar a alteração",

  // ----- Reports -----
  "Monthly reports": "Relatórios mensais",
  "Generate a report": "Gerar um relatório",
  "Month": "Mês",
  "Report language": "Idioma do relatório",
  "All companies (combined PDF)": "Todas as empresas (PDF combinado)",
  "All currencies": "Todas as moedas",
  "Generate": "Gerar",
  "Generating…": "A gerar…",
  "Open PDF": "Abrir o PDF",
  "Download PDF": "Transferir o PDF",
  "Emailing…": "A enviar…",
  "Personal": "Pessoal",
  "Open camera": "Abrir a câmara",
  "Pick photo(s) or PDF from files": "Escolher foto(s) ou PDF dos ficheiros",
  "Building PDF & uploading…": "A criar o PDF e enviar…",
  "Save & close": "Guardar e fechar",
  "Cur": "Moe",
  "Custom range…": "Período personalizado…",
  "Descriptions and categories are translated. Establishment names stay exactly as printed on the receipts.": "As descrições e categorias são traduzidas. Os nomes dos estabelecimentos permanecem exatamente como impressos nos recibos.",

  // ----- Settings -----
  "How this works": "Como funciona",
  "Searchable answers + ask-a-question box": "Respostas pesquisáveis + caixa de perguntas",
  "Team": "Equipa",
  "Manage team members": "Gerir os membros da equipa",
  "Add or remove people who can sign in": "Adicionar ou remover pessoas",
  "My profile": "O meu perfil",
  "Personal details": "Dados pessoais",
  "Name, address, bank details (used on invoices)": "Nome, morada, dados bancários (para as faturas)",
  "Edit": "Editar",
  "Trash": "Lixo",
  "Deleted receipts appear here for 30 days, then they're gone for good.": "Os recibos eliminados ficam aqui 30 dias e depois desaparecem definitivamente.",
  "Unknown vendor": "Fornecedor desconhecido",
  "Restore": "Restaurar",
  "Restoring…": "A restaurar…",
  "Companies": "Empresas",
  "Categories": "Categorias",
  "People": "Pessoas",
  "Currencies": "Moedas",
  "No entries yet.": "Ainda sem entradas.",
  "+ Add company": "+ Adicionar empresa",
  "Add a person": "Adicionar pessoa",
  "Currency name": "Nome da moeda",
  "New category name": "Nome da nova categoria",
  "No limit": "Sem limite",
  "Add": "Adicionar",
  "+ Add category": "+ Adicionar categoria",
  "Spending limit is per receipt. Anything over it gets flagged in Issues until the team member acknowledges it.": "O limite de despesa aplica-se por recibo. Qualquer excesso é sinalizado em Anomalias até o membro da equipa o confirmar.",
  "Categories are managed by the admin. Ask Carl to add a new one if you need it.": "As categorias são geridas pelo administrador. Peça ao Carl para adicionar uma nova se precisar.",

  // ----- My profile -----
  "App language": "Idioma da aplicação",
  "Identity": "Identidade",
  "Address": "Morada",
  "Payment details": "Dados de pagamento",
  "Payment instructions": "Instruções de pagamento",
  "Full name": "Nome completo",
  "Business name": "Nome da empresa",
  "Email": "E-mail",
  "Phone": "Telefone",
  "Address line 1": "Morada, linha 1",
  "Address line 2": "Morada, linha 2",
  "Country": "País",
  "VAT / Tax number": "Número de IVA",
  "Your full name": "O seu nome completo",
  "Street address": "Morada (rua)",
  "City, region, postcode": "Cidade, região, código postal",
  "Your country": "O seu país",
  "Optional": "Opcional",
  "Translating…": "A traduzir…",
  "Translate my existing receipt descriptions": "Traduzir as descrições dos meus recibos existentes",

  // ----- Prose, banners, dialogs, errors -----
  "Language": "Idioma",
  "Amount must be a positive number (e.g. 12.50). Letters aren't allowed.": "O montante deve ser um número positivo (ex. 12,50). Não são permitidas letras.",
  "Amount must be a positive number (e.g. 12.50).": "O montante deve ser um número positivo (ex. 12,50).",
  "Receipt date is in the future — please pick today or earlier.": "A data do recibo está no futuro — escolha hoje ou uma data anterior.",
  "Delete this receipt? It moves to Trash for 30 days, then it's gone for good.": "Eliminar este recibo? Vai para o Lixo durante 30 dias e depois desaparece definitivamente.",
  "OCR vs your edits — please review": "Leitura do recibo vs as suas alterações — verifique",
  "OCR failed to process this receipt. Fill in the amount, currency, and date manually below.": "A leitura automática deste recibo falhou. Preencha o montante, a moeda e a data manualmente abaixo.",
  "No amount was extracted from this receipt. Enter the amount manually in the Amount field below.": "Não foi extraído nenhum montante deste recibo. Introduza-o manualmente no campo Montante abaixo.",
  "Clicking confirms you're intentionally claiming this even though it matches another receipt. If it really is a duplicate, delete one instead.": "Clicar confirma que submete intencionalmente esta despesa apesar de corresponder a outro recibo. Se for mesmo um duplicado, elimine um deles.",
  "Clicking records that you're knowingly claiming an over-limit expense and clears the Issues flag. The acknowledgement stays on the receipt's record.": "Clicar regista que submete conscientemente uma despesa acima do limite e remove a sinalização. A confirmação fica no histórico do recibo.",
  "This receipt is": "Este recibo é de",
  "over the": "acima do limite de",
  "limit for": "para",
  "Over the spending limit": "Limite de despesa ultrapassado",
  "No internet detected.": "Sem ligação à internet.",
  "Photos taken in this app are not yet saved while offline — they'd be lost when the upload fails. As a fallback right now: take the photo with your phone's Camera app, then email it to": "As fotos tiradas na aplicação ainda não são guardadas offline — perder-se-iam se o envio falhar. Como alternativa: tire a foto com a câmara do telemóvel e envie-a por e-mail para",
  "Your mail app's outbox will queue and send it when you're back online.": "A sua aplicação de e-mail enviá-la-á automaticamente quando a ligação voltar.",
  "Open camera, take the first page, then tap \"+ Add another page\" on the preview to keep going. All pages are combined into one PDF receipt.": "Abra a câmara, tire a primeira página e toque em «+ Adicionar página» na pré-visualização para continuar. Todas as páginas são combinadas num único recibo PDF.",
  "in the file picker, tap-and-hold on iPhone or Cmd-click on Mac to select multiple files at once. Each becomes its own receipt.": "no seletor de ficheiros, mantenha premido (iPhone) ou Cmd-clique (Mac) para selecionar vários ficheiros de uma vez. Cada um torna-se um recibo distinto.",
  "No signal? Use your Camera app and email to": "Sem rede? Use a câmara e envie por e-mail para",
  "These details appear at the top of every monthly invoice (BILL FROM block) and in the payment-details footer.": "Estes dados aparecem no topo de cada fatura mensal e no rodapé dos dados de pagamento.",

  // ----- Help & FAQ page -----
  "Search the FAQ… (e.g. duplicate, email, tip)": "Pesquisar na FAQ… (ex. duplicado, e-mail, gorjeta)",
  "Nothing matches": "Sem resultados para",
  "Try the question box above, or email": "Experimente a caixa de perguntas acima, ou escreva a",
  "How do I…?": "Como faço…?",
  "Ask anything — e.g. how do I forward a receipt by email?": "Faça a sua pergunta — ex. como reencaminho um recibo por e-mail?",
  "Thinking…": "A pensar…",
  "Ask": "Perguntar",
  "Answers usage questions only — it can't see your receipts or change anything.": "Responde apenas a perguntas de utilização — sem acesso aos seus recibos e sem alterar nada.",

  // ----- Concierge -----
  "Concierge": "Concierge",
  "Send": "Enviar",
  "Message the Concierge…": "Escreva ao Concierge…",
  "Ask about your receipts…": "Pergunte sobre os seus recibos…",
  "Ask me about your expenses, or tell me to record one:": "Pergunte-me sobre as suas despesas, ou peça-me para registar uma:",
  "How much did I spend on meals last month?": "Quanto gastei em refeições no mês passado?",
  "Record a 4.50 coffee at Starbeans today, category Meals": "Regista um café de 4,50 no Starbeans hoje, categoria Refeições",
  "Which receipts still have issues?": "Que recibos ainda têm anomalias?",
  "I can only see and change YOUR receipts. Team and settings changes happen in Settings.": "Só vejo e altero OS SEUS recibos. A equipa e as definições gerem-se em Definições.",
  "Confirm delete": "Confirmar a eliminação",
  "Done — moved to Trash (restorable for 30 days).": "Feito — movido para o Lixo (restaurável durante 30 dias).",
  "Okay — cancelled, nothing was deleted.": "Certo — cancelado, nada foi eliminado.",
  "Smart AI": "Smart AI",

  "Receipt descriptions are also written in this language.": "As descrições dos recibos também serão escritas neste idioma.",
  // ----- Sweep 2026-07-04 (Carl's Portuguese test) -----
  "No receipts match these filters.": "Nenhum recibo corresponde a estes filtros.",
  "No receipts yet.": "Ainda não há recibos.",
  "Loading PDF…": "A carregar o PDF…",
  "OCR extracted": "Leitura do recibo",
  "You entered": "O que introduziu",
  "Tap a company name to edit address & full legal name (used in invoices).": "Toque no nome de uma empresa para editar a morada e o nome legal completo (usados nas faturas).",
  "Companies are managed by the admin. Ask Carl to add a new one if you need it.": "As empresas são geridas pelo administrador. Peça ao Carl para adicionar uma nova se precisar.",
  "Currencies are managed by the admin. Ask Carl to add a new one if you need it.": "As moedas são geridas pelo administrador. Peça ao Carl para adicionar uma nova se precisar.",
  "Free-form text — printed word-for-word in the PAYMENT DETAILS block of every invoice. Format it however your bank/country requires (IBAN + SWIFT, sort code + account, etc.).": "Texto livre — impresso tal e qual no bloco DADOS DE PAGAMENTO de cada fatura. Formate-o segundo as exigências do seu banco/país (IBAN + SWIFT, etc.).",
  "Optional — for tax-registered users": "Opcional — se estiver registado para IVA",
  "Optional — if you invoice through a company": "Opcional — se faturar através de uma empresa",
  "Contact email shown on invoice": "E-mail de contacto mostrado na fatura",
  "Rewrite the descriptions of ALL your existing receipts in this language? Vendor names, amounts and dates are never changed. This can't be undone.": "Reescrever as descrições de TODOS os seus recibos existentes neste idioma? Os nomes dos fornecedores, montantes e datas nunca mudam. Ação irreversível.",
  "descriptions translated.": "descrições traduzidas.",
  "Translation failed:": "Falha na tradução:",
  "Type a name — pick from the list, or hit Add to create": "Escreva um nome — escolha da lista ou toque em Adicionar",
  "Multi-page document": "Documento de várias páginas",
  "page": "página",
  "pages": "páginas",
  "Page": "Página",
  // ----- Guided tour (#48a) -----
  "Skip": "Ignorar",
  "Next": "Seguinte",
  "Got it!": "Entendido!",
  "Show me around again": "Repetir a visita guiada",
  "Add receipts": "Adicionar recibos",
  "Tap + Capture to photograph a receipt — the amount, vendor and date are read for you. You can also email receipts in, or add cash expenses manually.": "Toque em + Foto para fotografar um recibo — o montante, o fornecedor e a data são lidos por si. Também pode enviar recibos por e-mail ou registar despesas em dinheiro manualmente.",
  "Your counters": "Os seus contadores",
  "Receipts shows everything. Uncategorized still needs a company. Issues turns red when something needs your attention — tap it to see why.": "Recibos mostra tudo. Sem categoria ainda precisa de uma empresa. Anomalias fica vermelho quando algo precisa da sua atenção — toque para ver porquê.",
  "At month-end, generate a polished PDF of your expenses here — in the language of your choice.": "No fim do mês, gere aqui um PDF cuidado das suas despesas — no idioma que preferir.",
  "Your assistant": "O seu assistente",
  "Ask anything — \"how much did I spend on meals?\" — or tell it to record an expense for you. It only ever sees your own receipts.": "Pergunte o que quiser — «quanto gastei em refeições?» — ou peça-lhe para registar uma despesa. Só vê os seus próprios recibos.",
  "Need help?": "Precisa de ajuda?",
  "The ? opens the FAQ with an ask-a-question box. You can rerun this tour from there anytime.": "O ? abre a FAQ com uma caixa de perguntas. Pode repetir esta visita a partir daí em qualquer altura.",
  "Try reading it again": "Tentar ler novamente",
};
